import { Injectable } from "@angular/core";
import { MatLegacySnackBarRef as MatSnackBarRef } from "@angular/material/legacy-snack-bar";
import { FabricDesignSystemLibCommand, FDSSnackBarAxis, SnackbarType } from "@fabric-types/lib-fabric-design-system";
import { ContentGroupType, ContentValue } from "@fabric-types/svc-content";
import { select, Store } from "@ngrx/store";
import { AlertOutput, SnackBarComponentBase } from "@obg/mat.ui.core";
import { batchActions } from "@obg/ngrx-utils";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	AppContextSelector,
	AuthAction,
	AuthSelector,
	CORE_ROUTE,
	Jurisdiction,
	JurisdictionSelector,
	RealityCheckAction,
	RealityCheckConfigService,
	RealityCheckSelector,
	SessionAction,
} from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { CurrencyService, DeviceConfigService } from "@obg/ngx.global";
import { DocumentRef, TelemetryService } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { DgaRealityCheckComponent, RealityCheckContainer, SgaRealityCheckComponent } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, Observable, of } from "rxjs";
import { filter, map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { ArgentinaRealityCheckSnackBarComponent } from "../../snackbar/argentina/argentina-reality-check-snack-bar.component";
import { RealityCheckSnackBarComponent } from "../../snackbar/reality-check-snack-bar.component";
import { GTMRealityCheckAction, RealityCheckEvent } from "../../telemetry/ui-interaction-telemetry.model";

const REALITY_CHECK_ALERT_ID = "REALITY_CHECK_ALERT_ID";

@Injectable({
	providedIn: "root",
})
export class RealityCheckService {
	constructor(
		private alertService: AbstractTranslatableAlertService,
		private authAction: AuthAction,
		private store: Store<CoreContentState>,
		private realityCheckConfig: RealityCheckConfigService,
		private realityCheckSelector: RealityCheckSelector,
		private realityCheckAction: RealityCheckAction,
		private snackBarService: AbstractTranslatableSnackBarService,
		private telemetryService: TelemetryService,
		private sessionAction: SessionAction,
		private currencyService: CurrencyService,
		private deviceConfigService: DeviceConfigService,
		private docRef: DocumentRef
	) {}

	initialize$(): Observable<MatSnackBarRef<SnackBarComponentBase> | undefined | unknown> {
		return this.store.pipe(
			select(this.realityCheckSelector.lastRealityCheck()),
			ignoreNil(),
			withLatestFrom(
				this.store.select(JurisdictionSelector.get),
				this.store.select(this.realityCheckSelector.isGlobalRealityCheckEnabled())
			),
			switchMap(([lastRealityCheck, jurisdiction, isGlobalRealityCheckEnabled]) => {
				if (!isGlobalRealityCheckEnabled) {
					return of(undefined);
				}

				if (jurisdiction === Jurisdiction.Sga || jurisdiction === Jurisdiction.Dga) {
					const activeProductMap = this.realityCheckConfig.getRealityCheckActiveProductMap();
					return combineLatest([
						this.store.select(RouteSelector.isNavigating),
						this.store.select(RouteSelector.getCurrent),
						this.store.select(AppContextSelector.getProduct),
						this.store.select(AuthSelector.getSessionStartTimeInMillis),
					]).pipe(
						filter(
							([isNavigating, route, product, sessionStart]) =>
								!isNavigating &&
								sessionStart !== undefined &&
								Date.now() - sessionStart > 0 &&
								(route?.name === CORE_ROUTE.home || !!(product && activeProductMap[product]))
						),
						take(1),
						map(() => {
							if (jurisdiction === Jurisdiction.Sga) {
								this.handleSGAEvents(GTMRealityCheckAction.Seen);
							}

							this.alertService.open(
								{
									dialogConfig: { disableClose: true },
									mustBeInteracted: true,
									doNotShowAgainMessage: false,
									...(this.deviceConfigService.isDesktop()
										? {
												messageComponent:
													jurisdiction === Jurisdiction.Sga
														? SgaRealityCheckComponent
														: DgaRealityCheckComponent,
												messageComponentInit: (
													c: SgaRealityCheckComponent | DgaRealityCheckComponent
												) => (c.data = lastRealityCheck),
											}
										: {
												title: `${jurisdiction.toLowerCase()}-reality-check.title`,
												message: `${jurisdiction.toLowerCase()}-reality-check.message`,
											}),
									buttons: [
										{
											text: `${jurisdiction.toLowerCase()}-reality-check.button-continue`,
											handler:
												jurisdiction === Jurisdiction.Sga
													? () => this.handleSGAEvents(GTMRealityCheckAction.Continue)
													: undefined,
										},
										{
											text: `${jurisdiction.toLowerCase()}-reality-check.button-stop`,
											handler: () =>
												jurisdiction === Jurisdiction.Sga
													? this.handleSGAEvents(GTMRealityCheckAction.Logout)
													: this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home)),
										},
									],
								},
								[],
								{
									elapsedMinutes: lastRealityCheck.elapsedMinutes,
									totalBets: this.currencyService.format(lastRealityCheck.totalBets ?? 0),
									totalWins: this.currencyService.format(lastRealityCheck.totalWins ?? 0),
									totalLosses: this.currencyService.format(
										Math.max(
											0,
											(lastRealityCheck.totalBets ?? 0) - (lastRealityCheck.totalWins ?? 0)
										)
									),
									netWinning: this.currencyService.format(lastRealityCheck.netWinning),
								}
							);

							return undefined;
						})
					);
				}

				if (
					jurisdiction === Jurisdiction.Iaui ||
					jurisdiction === Jurisdiction.Lotba ||
					jurisdiction === Jurisdiction.Iplyc ||
					jurisdiction === Jurisdiction.LPCSE
				) {
					return this.snackBarService.openSnackBar$({
						message: `reality-check.message`,
						dismissible: false,
						keepOpen: true,
						buttons: [
							{
								text: "reality-check.dismiss",
								delayMs: 10000,
							},
						],
						snackBarComponent:
							jurisdiction === Jurisdiction.Iaui
								? RealityCheckSnackBarComponent
								: ArgentinaRealityCheckSnackBarComponent,
						panelClass: ["reality-check-snack-bar-container"],
					});
				}

				if (jurisdiction === Jurisdiction.Aams) {
					return new Observable(
						this.docRef.obgEventBus.retrieve(ContentValue.getTranslations, {
							key: ["reality-check.one-hour-prompt"],
							requiredGroup: [ContentGroupType.General],
						})
					).pipe(
						tap((translations) => {
							this.docRef.obgEventBus.dispatch(FabricDesignSystemLibCommand.SnackbarShow, {
								testId: "alert-REALITY_CHECK_ALERT_ID",
								text: translations["reality-check.one-hour-prompt"],
								showCloseButton: true,
								type: SnackbarType.Info,
								closeAutomatically: true,
								showProgressBar: true,
								timeLimit: 10000,
								yAxis: FDSSnackBarAxis.Top,
								xAxis: FDSSnackBarAxis.Center,
								showIcon: true,
							});
						})
					);
				}

				this.alertService.open(
					{
						id: REALITY_CHECK_ALERT_ID,
						hideClose: true,
						dialogConfig: { disableClose: true },
						doNotShowAgainMessage: true,
						mustBeInteracted: true,
						messageComponent: RealityCheckContainer,
						messageComponentInit: (c: RealityCheckContainer) => (c.data = lastRealityCheck),
						buttons: [
							{
								text: "reality-check.button-logout",
								handler: ({ doNotShowAgain }: AlertOutput) => {
									this.store.dispatch(this.authAction.logout());
									this.handleRealityCheckClose(doNotShowAgain);
									this.trackOverlayMessageEvent(jurisdiction, GTMRealityCheckAction.Logout);
								},
							},
							{
								text: "reality-check.button-ok",
								handler: ({ doNotShowAgain }: AlertOutput) => {
									this.handleRealityCheckClose(doNotShowAgain);
									this.trackOverlayMessageEvent(jurisdiction, GTMRealityCheckAction.Continue);
								},
							},
						],
					},
					[]
				);
				this.trackOverlayMessageEvent(jurisdiction, GTMRealityCheckAction.Seen);
				return of(undefined);
			})
		);
	}

	private handleRealityCheckClose(doNotShowAgain: boolean) {
		if (doNotShowAgain) {
			this.store.dispatch(this.realityCheckAction.disableRealityCheck());
		}
	}

	private trackOverlayMessageEvent(jurisdiction: Jurisdiction | undefined, eventAction: GTMRealityCheckAction) {
		this.telemetryService.track({
			event: RealityCheckEvent.OverlayMessage,
			Action: eventAction,
			Jurisdiction: jurisdiction,
		});
		if (eventAction === GTMRealityCheckAction.Seen) {
			return;
		}
		this.telemetryService.track({
			event: RealityCheckEvent.OverlayMessage,
			Action: GTMRealityCheckAction.Hide,
			Jurisdiction: jurisdiction,
		});
	}

	private handleSGAEvents(event: GTMRealityCheckAction) {
		const trackOverlayMessageEvent = (action: GTMRealityCheckAction) => {
			this.telemetryService.track({
				event: RealityCheckEvent.OverlayMessage,
				Action: action,
				Jurisdiction: Jurisdiction.Sga,
			});
		};
		if (event === GTMRealityCheckAction.Continue) {
			trackOverlayMessageEvent(GTMRealityCheckAction.Continue);
			trackOverlayMessageEvent(GTMRealityCheckAction.Hide);
		} else if (event === GTMRealityCheckAction.Logout) {
			trackOverlayMessageEvent(GTMRealityCheckAction.Logout);
			trackOverlayMessageEvent(GTMRealityCheckAction.Hide);
			this.store.dispatch(batchActions(RouteAction.navigate(CORE_ROUTE.home), this.sessionAction.logout()));
		} else if (event === GTMRealityCheckAction.Seen) {
			trackOverlayMessageEvent(GTMRealityCheckAction.Seen);
		}
	}
}
